import { useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Mail, AlertCircle, RefreshCw, Globe } from "lucide-react";
import { ProposalI18nProvider, useProposalT } from "./i18n/ProposalI18nContext";
import { LanguageSelector } from "./components/LanguageSelector";
import WobaLogo from "@/assets/woba.svg";
import type { ProposalPayload, RankedSpace } from "../ai-mode/types";
import {
  DEFAULT_CONTRACT_TERM,
  DEFAULT_LICENSE_FEE_MODE,
  DEFAULT_LICENSE_FEE_PERCENTAGE,
} from "../ai-mode/types";
import { calculateLicenseFeeFromAdjustments } from "../ai-mode/utils/licenseFee";
import {
  buildProposalSharedContext,
  currentDevice,
} from "../ai-mode/utils/proposalSharedContext";
import {
  buildRoomTrackingContextMap,
  roomTrackingContextFor,
} from "../ai-mode/utils/roomTrackingContext";
import { mergeDuplicateSpaces } from "../proposal-standard/utils";
import { HeroSection } from "./components/HeroSection";
import {
  BeforeAfterSection,
  FlexibilitySection,
  NextStepsSection,
} from "./components/ProposalSection";
import { CTASection, LogosSection } from "./components/CTASection";
import { NetworkSection } from "./components/NetworkSection";
import { SpacesSection } from "./components/SpacesSection";
import { SpaceComparisonTable } from "./components/SpaceComparisonTable";
import { FlexCreditsSection } from "./components/FlexCreditsSection";
import { LicenseFeeSection } from "./components/LicenseFeeSection";
import { SetupFeeSection } from "./components/SetupFeeSection";
import { ComparisonSection } from "./components/ComparisonSection";
import { ScheduleModal } from "./components/ScheduleModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { visionService } from "@/services/vision-service";
import { loadProposal } from "../proposal-standard/loadProposal";
import {
  sendAnonymousIdentifyData,
  sendTrackDataServer,
} from "@/features/analytics/utils/serverTrackData";
import { MOCK_PROPOSAL_RESUMED } from "./mock-proposal";

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isBlockedEmail(email: string): boolean {
  const localPart = email.toLowerCase().split("@")[0] ?? "";
  return /^teste?$/.test(localPart);
}

function isInternalEmail(email: string): boolean {
  return email.toLowerCase().endsWith("@woba.com.br");
}

function notifySlackProposalAccess(email: string, proposalId: string) {
  if (import.meta.env.VITE_NODE_ENV !== "production") return;
  if (isInternalEmail(email)) return;

  const proposalBaseUrl =
    import.meta.env.VITE_VISION_PROPOSAL_URL ??
    `${window.location.origin}/vision/proposta`;
  const proposalLink = `${proposalBaseUrl}/${proposalId}`;

  visionService.notifySlack(
    "proposal_viewed",
    `📬 Proposta acessada\n• E-mail: ${email}\n• Proposta: ${proposalLink}`,
  );
}

// Fired after the payload loads (not on the access attempt) so the shared
// context — deal_id above all — is attributable, matching the SMB page.
function trackProposalView(
  email: string,
  proposalId: string,
  proposal: ProposalPayload | null,
) {
  sendTrackDataServer({
    eventName: "VISION_PROPOSAL",
    productType: "PO",
    url: window.location.pathname,
    data: {
      email,
      proposal_id: proposalId,
      user_agent: navigator.userAgent,
      language: navigator.language,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...buildProposalSharedContext(proposal, currentDevice()),
    },
  });
}

interface ProposalPageProps {
  /** The payload ProposalRoutePage already fetched to pick this template —
   * reused here so revealing doesn't refetch what the route just loaded.
   * Absent (direct render, tests): the reveal fetches as before. */
  initialProposal?: ProposalPayload | null;
}

export default function ProposalPage({ initialProposal }: ProposalPageProps) {
  const { uuid } = useParams<{ uuid: string }>();

  return (
    <ProposalI18nProvider>
      {uuid === "preview" ? (
        <ProposalContent proposal={MOCK_PROPOSAL_RESUMED} email="" />
      ) : (
        <LiveProposalFlow uuid={uuid} initialProposal={initialProposal} />
      )}
    </ProposalI18nProvider>
  );
}

function LiveProposalFlow({
  uuid,
  initialProposal,
}: {
  uuid: string | undefined;
  initialProposal?: ProposalPayload | null;
}) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [proposal, setProposal] = useState<ProposalPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // One access event per session, even if the client retries after a failure.
  const viewTrackedRef = useRef(false);
  const { t } = useProposalT();

  function trackViewOnce(data: ProposalPayload | null) {
    if (viewTrackedRef.current || !uuid) return;
    viewTrackedRef.current = true;
    trackProposalView(email, uuid, data);
  }

  // Single load path for reveal and retry so post-load side effects (track,
  // reveal, error handling) can never diverge between the two.
  async function loadAndReveal() {
    if (!uuid) return;
    setLoading(true);
    setLoadError(false);
    try {
      const data = initialProposal ?? (await loadProposal(uuid));
      setProposal(data);
      setRevealed(true);
      trackViewOnce(data);
    } catch (error) {
      console.error("Failed to load proposal:", error);
      setLoadError(true);
      setEmailError(t("emailGate.proposalLoadError"));
    } finally {
      setLoading(false);
    }
  }

  async function handleReveal() {
    // Enter has no disabled state (only the button does) — bail while a
    // reveal is in flight so it can't double-fetch or double-notify Slack.
    if (loading) return;
    if (!isValidEmail(email)) {
      setEmailError(t("emailGate.invalidEmail"));
      return;
    }
    if (isBlockedEmail(email)) {
      setEmailError(t("emailGate.blockedEmail"));
      return;
    }
    setEmailError("");
    void sendAnonymousIdentifyData(email);
    if (uuid) notifySlackProposalAccess(email, uuid);
    await loadAndReveal();
  }

  async function handleRetry() {
    setEmailError("");
    await loadAndReveal();
  }

  if (!revealed) {
    return (
      <>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        <div
          style={{
            height: "100dvh",
            background: "#F5F3EF",
            fontFamily: "'Space Grotesk', sans-serif",
            position: "relative",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Blurred gradient teaser background */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, #FFD900 0%, #FFE94D 30%, #FDFBF7 70%)",
              filter: "blur(40px) brightness(0.95)",
              opacity: 0.6,
              zIndex: 0,
            }}
          />

          <div
            className="flex-1 overflow-y-auto flex flex-col items-center justify-center px-4 py-4 sm:py-8"
            style={{ position: "relative", zIndex: 1, minHeight: 0 }}
          >
            <EmailGate
              email={email}
              emailError={emailError}
              loading={loading}
              loadError={loadError}
              onEmailChange={(v) => {
                setEmail(v);
                setEmailError("");
              }}
              onReveal={handleReveal}
              onRetry={handleRetry}
            />
          </div>

          <footer
            className="px-6 text-xs flex-shrink-0"
            style={{
              color: "#B8B3AD",
              borderTop: "1px solid rgba(0,0,0,0.05)",
              position: "relative",
              zIndex: 1,
              paddingTop: "12px",
              paddingBottom: "max(12px, env(safe-area-inset-bottom))",
            }}
          >
            <div className="flex flex-col sm:flex-row sm:relative items-center justify-center gap-2 sm:gap-0 max-w-6xl mx-auto">
              <span>
                {t("emailGate.footer")} &nbsp;·&nbsp; {new Date().getFullYear()}
              </span>
              <div className="sm:absolute sm:right-0 flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-[#9E9A94]" />
                <LanguageSelector />
              </div>
            </div>
          </footer>
        </div>
      </>
    );
  }

  return <ProposalContent proposal={proposal} email={email} />;
}

function EmailGate({
  email,
  emailError,
  loading,
  loadError,
  onEmailChange,
  onReveal,
  onRetry,
}: {
  email: string;
  emailError: string;
  loading: boolean;
  loadError: boolean;
  onEmailChange: (v: string) => void;
  onReveal: () => void;
  onRetry: () => void;
}) {
  const { t } = useProposalT();
  return (
    <div
      className="w-full max-w-sm rounded-2xl p-5 sm:p-8 space-y-4 sm:space-y-6"
      style={{
        background: "rgba(255,255,255,0.95)",
        backdropFilter: "blur(12px)",
        border: "1px solid #E8E6E3",
        boxShadow: "0 8px 40px rgba(0,0,0,0.08)",
        animation: "fadeIn 0.5s cubic-bezier(0.16,1,0.3,1)",
      }}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <img src={WobaLogo} alt="Woba" className="h-6 w-auto" />
        <p
          style={{
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: "#B8860B",
            margin: 0,
          }}
        >
          {t("emailGate.exclusiveProposal")}
        </p>
      </div>

      <div className="h-px w-full" style={{ background: "#F0EEEB" }} />

      <div className="text-center">
        <p className="text-sm font-medium" style={{ color: "#78736D" }}>
          {t("emailGate.yourProposalIsReady")}
        </p>
        <p className="text-xs mt-1" style={{ color: "#9E9A94" }}>
          {t("emailGate.enterEmail")}
        </p>
      </div>

      <div className="space-y-4">
        {loadError ? (
          <>
            <div className="flex flex-col items-center gap-3 text-center">
              <AlertCircle className="w-10 h-10 text-red-500" />
              <p className="text-sm font-medium text-[#1A1814]">
                {t("emailGate.notFound")}
              </p>
              <p className="text-xs text-[#78736D]">
                {t("emailGate.loadError")}
              </p>
            </div>
            <button
              type="button"
              onClick={onRetry}
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-bold transition-all duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                background: "#FFD900",
                color: "#1A1814",
                border: "none",
              }}
            >
              {loading ? (
                t("emailGate.loading")
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  {t("emailGate.retry")}
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <div className="relative">
                <Mail
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                  style={{ color: "#B8B3AD" }}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onReveal()}
                  placeholder={t("emailGate.placeholder")}
                  className="w-full pl-10 pr-4 py-3 rounded-xl text-sm focus:outline-none transition-all"
                  style={{
                    background: "#FDFBF7",
                    border: emailError
                      ? "1px solid rgba(239,68,68,0.6)"
                      : "1px solid #E8E6E3",
                    color: "#1A1814",
                  }}
                />
              </div>
              {emailError && (
                <div className="flex items-center gap-1.5 text-red-500 text-xs">
                  <AlertCircle className="w-3 h-3" />
                  {emailError}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={onReveal}
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-bold transition-all duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "#FFD900",
                color: "#1A1814",
                border: "none",
              }}
            >
              {loading ? t("emailGate.loading") : t("emailGate.accessProposal")}
            </button>
          </>
        )}
      </div>

      {/* Social proof */}
      <div
        className="flex items-center justify-center"
        style={{ opacity: 0.4 }}
      >
        <span style={{ fontSize: "11px", fontWeight: 600, color: "#78736D" }}>
          {t("emailGate.socialProof")}
        </span>
      </div>
    </div>
  );
}

export function ProposalContent({
  proposal,
  email,
  hidePrice = false,
  isPartnerMode = false,
}: {
  proposal: ProposalPayload | null;
  email: string;
  hidePrice?: boolean;
  isPartnerMode?: boolean;
}) {
  const { state, suggestedSpaces = [] } = (proposal ?? {
    state: null,
    suggestedSpaces: [],
  }) as {
    state: ProposalPayload["state"] | null;
    suggestedSpaces: RankedSpace[];
  };

  const { t } = useProposalT();
  const diagnosis = state?.diagnosis;
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>(
    suggestedSpaces[0]?.id ?? "",
  );
  const [visitSelectedIds, setVisitSelectedIds] = useState<Set<string>>(
    new Set(),
  );
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [partnerDemoScheduleModalOpen, setPartnerDemoScheduleModalOpen] =
    useState(false);
  const [scheduledSpaceIds, setScheduledSpaceIds] = useState<Set<string>>(
    new Set(),
  );

  const handleSchedule = isPartnerMode
    ? () => setPartnerDemoScheduleModalOpen(true)
    : () => setScheduleModalOpen(true);

  const handleToggleVisit = (id: string) => {
    setVisitSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visitSelectedSpaces = suggestedSpaces.filter((s) =>
    visitSelectedIds.has(s.id),
  );
  const proposalValidity = state?.adjustments?.proposalValidity;
  const showScheduling = proposal?.proposalType !== "ENT";

  // Ranking context for the scheduling conversion event. Positions come from
  // the FULL deduped ranked list (same definition as the SMB provider), not
  // from the visit-selected subset the modal receives.
  const roomTrackingContextBySpaceId = useMemo(
    () => buildRoomTrackingContextMap(mergeDuplicateSpaces(suggestedSpaces)),
    [suggestedSpaces],
  );

  const licenseFeeSection = useMemo(() => {
    if (!state?.adjustments?.showLicenseFee) return null;
    const adjs = state.adjustments;
    const value =
      adjs.licenseFeeValue ??
      calculateLicenseFeeFromAdjustments(
        adjs.licenseFeeMode ?? DEFAULT_LICENSE_FEE_MODE,
        adjs.licenseFeePercentage ?? DEFAULT_LICENSE_FEE_PERCENTAGE,
        adjs.licenseFeeFixed ?? 0,
        adjs.spaceAdjustments ?? [],
        adjs.contractMonths ?? DEFAULT_CONTRACT_TERM,
      ).value;
    if (value <= 0) return null;
    const period =
      adjs.licenseFeePeriod ??
      (adjs.contractMonths === 6 ? "semiannual" : "annual");
    return <LicenseFeeSection value={value} period={period} />;
  }, [state?.adjustments]);

  const setupFeeSection = useMemo(() => {
    const adjs = state?.adjustments;
    if (!adjs?.includeSetupFee) return null;
    const value = adjs.setupFee ?? 0;
    if (value <= 0) return null;
    return <SetupFeeSection value={value} />;
  }, [state?.adjustments]);

  if (!proposal) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#F5F3EF" }}
      >
        <div
          className="rounded-2xl p-6 text-center space-y-2"
          style={{
            background: "#FFFFFF",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <AlertCircle className="w-8 h-8 mx-auto" style={{ color: "#666" }} />
          <p className="text-sm" style={{ color: "#666" }}>
            {t("proposal.notFoundOrExpired")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;900&display=swap');
        @keyframes marqueeLeft {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marqueeRight {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `}</style>
      <div
        className="overflow-x-hidden"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        <HeroSection
          companyName={diagnosis?.companyName}
          targetCity={diagnosis?.targetCity}
          proposalValidity={proposalValidity?.toString()}
          contractMonths={state?.adjustments?.contractMonths ?? undefined}
          spaces={suggestedSpaces}
          spaceAdjustments={state?.adjustments?.spaceAdjustments}
          isPartnerMode={isPartnerMode}
        />
        <ComparisonSection />
        <SpacesSection
          spaces={suggestedSpaces}
          companyName={diagnosis?.companyName}
          targetCity={diagnosis?.targetCity}
          spaceAdjustments={state?.adjustments?.spaceAdjustments}
          selectedSpaceId={selectedSpaceId}
          onSelectSpace={setSelectedSpaceId}
          proposalId={proposal?.uuid ?? ""}
          dealId={diagnosis?.dealId}
          userEmail={email}
          clientSegment={diagnosis?.segment}
          suppressTracking={isPartnerMode}
          showLicenseFee={state?.adjustments?.showLicenseFee ?? false}
          hidePrice={hidePrice}
        />
        <SpaceComparisonTable
          spaces={suggestedSpaces}
          spaceAdjustments={state?.adjustments?.spaceAdjustments}
          selectedSpaceId={selectedSpaceId}
          onSelectSpace={setSelectedSpaceId}
          visitSelectedIds={visitSelectedIds}
          onToggleVisit={handleToggleVisit}
          onSchedule={handleSchedule}
          scheduledSpaceIds={scheduledSpaceIds}
          showScheduling={showScheduling}
          hidePrice={hidePrice}
        />
        {(licenseFeeSection ||
          setupFeeSection ||
          state?.adjustments?.includeFlexCredits) && (
          <div className="px-4 md:px-6 font-space-grotesk bg-[#FFFDF5]">
            <div className="max-w-6xl mx-auto py-5 flex justify-center">
              <div className="flex flex-col md:flex-row items-stretch gap-12">
                {licenseFeeSection}
                {setupFeeSection}
                {state?.adjustments?.includeFlexCredits && (
                  <FlexCreditsSection
                    flexCredits={state?.adjustments?.flexCredits ?? 0}
                    creditsPrice={state?.adjustments?.creditsPrice}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        <BeforeAfterSection />
        <LogosSection />
        <NetworkSection />
        <FlexibilitySection />
        <NextStepsSection
          showScheduling={showScheduling}
          visitSelectedIds={visitSelectedIds}
          onSchedule={handleSchedule}
        />
        <CTASection
          proposalValidity={proposalValidity?.toString()}
          showScheduling={showScheduling}
          visitSelectedIds={visitSelectedIds}
          onSchedule={handleSchedule}
          createdAt={proposal.generatedAt}
        />
      </div>

      <Dialog
        open={partnerDemoScheduleModalOpen}
        onOpenChange={setPartnerDemoScheduleModalOpen}
      >
        <DialogContent className="max-w-md text-center font-space-grotesk">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto"
            style={{ background: "#FFD900" }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1A1814"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 14 19.79 19.79 0 0 1 1.61 5.38 2 2 0 0 1 3.6 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.9a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 18.18v3z" />
            </svg>
          </div>
          <DialogTitle className="sr-only">Agendar visitas</DialogTitle>
          <DialogDescription
            className="text-sm leading-relaxed"
            style={{ color: "#3C2110" }}
          >
            Neste momento, seriam enviadas comunicações para os Parceiros
            selecionados, verificando a disponibilidade da visita às suas salas.
          </DialogDescription>
        </DialogContent>
      </Dialog>

      {showScheduling && scheduleModalOpen && proposalValidity != null && (
        <ScheduleModal
          proposalId={proposal?.uuid ?? ""}
          dealId={diagnosis?.dealId}
          clientSegment={diagnosis?.segment}
          roomContextFor={(spaceId) =>
            roomTrackingContextFor(roomTrackingContextBySpaceId, spaceId)
          }
          isOpen={scheduleModalOpen}
          spaces={visitSelectedSpaces}
          validityMs={proposalValidity}
          createdAt={proposal?.generatedAt}
          visitorEmail={email || diagnosis?.contactEmail}
          proposalOrigin={proposal?.proposalOrigin}
          onScheduled={(ids) => {
            setScheduledSpaceIds((prev) => new Set([...prev, ...ids]));
          }}
          onClose={() => {
            setScheduleModalOpen(false);
            setVisitSelectedIds(new Set());
          }}
        />
      )}
    </>
  );
}
